import { useState, useEffect, useRef } from "react";
import "./usercss/notifications.css";
import "./usercss/UserProfile.css";
import { RiMegaphoneLine } from "react-icons/ri";
import { FiBook, FiCalendar, FiLock, FiTrash2, FiAlertTriangle, FiClock, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { db, auth } from "../../firebase";
import { collection, query, where, onSnapshot, orderBy, doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import educationIcon from "../../images/education.png";
import eventsIcon from "../../images/events.png";
import announcementIcon from "../../images/announcement.png";
import { userService, notificationService } from "../../services/firestoreService";
import { useLanguage } from "../../contexts/LanguageContext";
import { translations } from "../../translations";

export default function Notifications({ setActiveTab }) {
  const { language } = useLanguage();
  const t = translations[language];
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [contentNotifications, setContentNotifications] = useState([]);
  const [publishedNotifs, setPublishedNotifs] = useState([]);
  const [userPreferences, setUserPreferences] = useState(null);
  const [userCreatedAt, setUserCreatedAt] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [notifToDelete, setNotifToDelete] = useState(null);
  const [notificationPreferences, setNotificationPreferences] = useState({
    announcementNotif: true,
    appointmentNotif: true,
    dataPrivacyNotif: true,
    contentNotif: true,
    contentNotifEnabledAt: null,
    dataPrivacyNotifEnabledAt: null,
    appointmentNotifEnabledAt: null,
    announcementNotifEnabledAt: null,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const itemsPerPage = 10;

  // Debounce mechanism for mark-as-read operations
  const markAsReadTimeoutRef = useRef(null);
  const markedAsReadSet = useRef(new Set());

  // Debounced mark-as-read function
  const debouncedMarkAsRead = (uid, notifId) => {
    // If already marked as read recently, skip
    if (markedAsReadSet.current.has(notifId)) {
      return;
    }
    
    // Add to set to prevent duplicate writes
    markedAsReadSet.current.add(notifId);
    
    // Clear existing timeout
    if (markAsReadTimeoutRef.current) {
      clearTimeout(markAsReadTimeoutRef.current);
    }
    
    // Set new timeout to batch writes
    markAsReadTimeoutRef.current = setTimeout(async () => {
      try {
        const user = auth.currentUser;
        if (user) {
          // Mark all pending notifications as read in a single batch
          const notifIds = Array.from(markedAsReadSet.current);
          await notificationService.markMultipleAsRead(user.uid, notifIds);
          markedAsReadSet.current.clear();
        }
      } catch (error) {
        console.error("Error marking notifications as read:", error);
        markedAsReadSet.current.clear();
      }
    }, 500); // 500ms debounce delay
  };

  // Fetch user's notification preferences
  useEffect(() => {
    const fetchSettings = async () => {
      const user = auth.currentUser;
      if (!user) return;
      try {
        const userData = await userService.getUser(user.uid);
        
        if (userData && userData.notificationPreferences) {
          setNotificationPreferences(prev => ({
            ...prev,
            ...userData.notificationPreferences
          }));
        }

        // Store user creation date for content filtering
        if (userData && userData.createdAt) {
          setUserCreatedAt(userData.createdAt);
        }
        
        // Check content preferences
        if (userData && userData.contentPreferences) {
          setUserPreferences(userData.contentPreferences);
        }
      } catch (err) {
        console.error("❌ Error fetching notification preferences:", err);
      }
    };
    fetchSettings();
  }, []);

  // Real-time listener for user-specific notification documents - OPTIMIZED
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      return;
    }

    let mounted = true;

    // Use notificationService listener for persistent Firestore notifications
    const unsubscribeNotifs = notificationService.getUserNotificationsListener(user.uid, async (notifications) => {
      if (!mounted) return;
      const enrichedNotifications = await Promise.all(notifications.map(async (notification) => {
        if (
          (notification.type === "data_access_request" || notification.type === "data_access_termination") &&
          !notification.professionalEmail &&
          notification.professionalId
        ) {
          try {
            const professionalSnap = await getDoc(doc(db, "users", notification.professionalId));
            return {
              ...notification,
              professionalEmail: professionalSnap.exists()
                ? professionalSnap.data().email || ""
                : ""
            };
          } catch (error) {
            console.error("Error fetching notification requester email:", error);
          }
        }
        return notification;
      }));
      if (!mounted) return;
      setContentNotifications(enrichedNotifications);
      setIsLoading(false);
      setHasLoaded(true);
    });

    return () => {
      mounted = false;
      unsubscribeNotifs();
    };
  }, []);

  // Helper to check if a notification should be shown based on "time gate" logic
  const shouldShowNotification = (notif, typeKey) => {
    // If we don't have preferences yet, show it (safety)
    if (!notificationPreferences) return true;

    const isEnabled = notificationPreferences[typeKey] !== false;
    const enabledAt = notificationPreferences[`${typeKey}EnabledAt`];
    const disabledAt = notificationPreferences[`${typeKey}DisabledAt`];
    
    const createdAt = notif.createdAt instanceof Date ? notif.createdAt : (notif.createdAt?.toDate ? notif.createdAt.toDate() : new Date(notif.createdAt));
    const enabledAtDate = enabledAt ? (enabledAt.toDate ? enabledAt.toDate() : new Date(enabledAt)) : null;
    const disabledAtDate = disabledAt ? (disabledAt.toDate ? disabledAt.toDate() : new Date(disabledAt)) : null;

    // 1. If currently ON: show it if it was created after we last turned it ON,
    // OR if it was created before we last turned it OFF (past notifications).
    if (isEnabled) {
      if (enabledAtDate && createdAt < enabledAtDate) {
        // Created before last ON toggle. 
        // Show if it was also before last OFF toggle (i.e. it's a "past notification")
        if (disabledAtDate && createdAt < disabledAtDate) {
          return true;
        }
        // This was created while it was OFF (backfilling scenario or edge case)
        return false;
      }
      return true;
    } else {
      // 2. If currently OFF: only show it if it was created before we turned it OFF.
      if (disabledAtDate && createdAt < disabledAtDate) {
        return true;
      }
      // This was created after it was turned OFF
      return false;
    }
  };

  // Build notifications arrays
  const privacyNotifications = contentNotifications
    .filter(n => n.type === "data_access_request" || n.type === "data_access_termination")
    .filter(n => shouldShowNotification(n, 'dataPrivacyNotif'))
    .map(notif => ({
    id: notif.id,
    icon: notif.type === "data_access_request" ? <FiLock style={{ color: '#ef4444' }} /> : <FiClock style={{ color: '#6b7280' }} />,
    title: notif.notificationsTitle || (notif.type === "data_access_request" ? "Data Access Request" : "Data Access Update"),
    desc: notif.type === "data_access_request" && (notif.professionalEmail || notif.proEmail)
      ? `${notif.professionalEmail || notif.proEmail} is requesting access to your records.`
      : notif.type === "data_access_termination" && notif.professionalEmail
        ? notif.message.replace(`(ID: ${notif.professionalId})`, `(${notif.professionalEmail})`)
        : notif.message,
    time: notif.createdAt.toLocaleString(),
    highlight: notif.status === "unread",
    isAccessRequest: notif.type === "data_access_request",
    onClick: async () => {
      try {
        const user = auth.currentUser;
        if (user && notif.status === "unread") {
          debouncedMarkAsRead(user.uid, notif.id);
        }
        
        // Navigation logic
        if (notif.type === "data_access_request") {
          // Navigate to user-home and open profile panel with data & privacy panel
          if (setActiveTab) setActiveTab('profile');
          localStorage.setItem('activeProfilePanel', 'privacy');
          navigate('/user-home', {
            state: {
              openProfilePanel: true,
              activePanel: 'privacy',
              activePrivacyTab: 'pending'
            }
          });
        } else {
          // data_access_termination - Navigate to user-home and open profile panel with access history panel
          if (setActiveTab) setActiveTab('profile');
          localStorage.setItem('activeProfilePanel', 'accesshistory');
          navigate('/user-home', { state: { openProfilePanel: true, activePanel: 'accesshistory' } });
        }
      } catch (error) {
        console.error("Error marking privacy notification as read:", error);
      }
    },
    onDelete: (e) => {
      e.stopPropagation();
      setNotifToDelete(notif);
      setShowDeleteModal(true);
    }
  }));
  const unreadPrivacyNotifications = privacyNotifications.filter(n => n.highlight);

  const appointmentNotifications = contentNotifications
    .filter(n => n.type === "appointment")
    .filter(n => shouldShowNotification(n, 'appointmentNotif'))
    .map(notif => ({
    id: notif.id,
    icon: notif.notificationsTitle === "Appointment Cancelled"
      ? <FiAlertTriangle style={{ color: '#ef4444' }} />
      : <FiCalendar style={{ color: '#10b981' }} />,
    title: notif.notificationsTitle || notif.title,
    desc: notif.message,
    time: notif.createdAt.toLocaleString(),
    highlight: notif.status === "unread",
    isAppointment: true,
    appointmentReference: notif.referenceNumber,
    onClick: async () => {
      try {
        const user = auth.currentUser;
        if (user && notif.status === "unread") {
          debouncedMarkAsRead(user.uid, notif.id);
        }
        if (setActiveTab) setActiveTab('appointment');
        navigate('/user-home', { state: { activeTab: 'appointment' } });
      } catch (error) {
        console.error("Error marking appointment notification as read:", error);
      }
    },
    onDelete: (e) => {
      e.stopPropagation();
      setNotifToDelete(notif);
      setShowDeleteModal(true);
    }
  }));
  const unreadAppointmentNotifications = appointmentNotifications.filter(n => n.highlight);

  const announcementsNotifications = contentNotifications
    .filter(n => n.type === "announcement")
    .filter(n => shouldShowNotification(n, 'announcementNotif'))
    .map(notif => ({
      id: notif.id,
      icon: <RiMegaphoneLine style={{ color: '#f59e0b' }} />,
      title: notif.notificationsTitle || notif.title,
      desc: notif.message,
      time: notif.createdAt.toLocaleString(),
      highlight: notif.status === "unread",
      onClick: async () => {
        try {
          const user = auth.currentUser;
          if (user && notif.status === "unread") {
            debouncedMarkAsRead(user.uid, notif.id);
          }
        } catch (error) {
          console.error("Error marking announcement notification as read:", error);
        }
      },
      onDelete: (e) => {
        e.stopPropagation();
        setNotifToDelete(notif);
        setShowDeleteModal(true);
      }
    }));
  const unreadAnnouncementsNotifications = announcementsNotifications.filter(n => n.highlight);

  const contentNotificationsList = contentNotifications
    .filter(n => n.type === "content" || (n.type !== "appointment" && n.type !== "data_access_request" && n.type !== "data_access_termination" && n.type !== "announcement"))
    .filter(n => shouldShowNotification(n, 'contentNotif'))
    .filter(notification => {
      // Filter content notifications by user's content preferences
      // If no content preferences, show all content notifications
      if (!userPreferences || !userPreferences.selectedTopics || userPreferences.selectedTopics.length === 0) {
        return true;
      }
      
      // Create mapping from category names to topic IDs
      const categoryToTopicId = {
        'Menstrual Wellness and Hygiene': 1,
        'Contraception': 2,
        'Fertility Basics': 3,
        'STI Awareness': 4,
        'Mental Health in Reproductive Care': 5,
        'Male Reproductive Health': 6,
        'Pregnancy Prevention': 7,
        'General Reproductive Wellness': 8,
        'Community Outreach Program': 9,
        'Community Outreach Programs': 9
      };
      
      const contentCategory = notification.category;
      const contentTopicId = categoryToTopicId[contentCategory];
      const selectedTopics = userPreferences.selectedTopics;
      
      // Check for match (either ID or name)
      const isIdMatch = contentTopicId && selectedTopics.includes(contentTopicId);
      const isNameMatch = userPreferences.topics && userPreferences.topics.some(t => t.trim().toLowerCase() === contentCategory.trim().toLowerCase());
      
      return isIdMatch || isNameMatch;
    })
    .map(notif => ({
      id: notif.id,
      icon: <FiBook style={{ color: '#3b82f6' }} />,
      title: notif.notificationsTitle || notif.title,
      desc: notif.message || `New content in ${notif.category}`,
      time: notif.createdAt.toLocaleString(),
      highlight: notif.status === "unread",
      isContentNotification: true,
      contentId: notif.contentId,
      category: notif.category,
      coverImage: notif.coverImage,
      onClick: async () => {
        // Mark as read in Firestore
        try {
          const user = auth.currentUser;
          if (user && notif.status === "unread") {
            await notificationService.markAsRead(user.uid, notif.id);
          }
          // Navigate to learn tab or specific content
          if (setActiveTab) setActiveTab('learn');
        } catch (error) {
          console.error("Error marking notification as read:", error);
        }
      },
      onDelete: (e) => {
        e.stopPropagation(); // Prevent trigger onClick
        setNotifToDelete(notif);
        setShowDeleteModal(true);
      }
    }));
  const unreadContentNotifications = contentNotificationsList.filter(n => n.highlight);

  const confirmDelete = async () => {
    if (!notifToDelete) return;
    try {
      const user = auth.currentUser;
      if (user) {
        await notificationService.deleteNotification(user.uid, notifToDelete.id);
      }
      setShowDeleteModal(false);
      setNotifToDelete(null);
    } catch (error) {
      console.error("Error deleting notification:", error);
    }
  };

  const allNotifications = [
    ...privacyNotifications,
    ...appointmentNotifications,
    ...announcementsNotifications,
    ...contentNotificationsList
  ];

  const getFilteredNotifications = () => {
    switch (activeCategory) {
      case "privacy": return privacyNotifications;
      case "appointments": return appointmentNotifications;
      case "announcements": return announcementsNotifications;
      case "content": return contentNotificationsList;
      default: return allNotifications;
    }
  };

  const filtered = getFilteredNotifications();
  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedItems = filtered.slice(startIndex, endIndex);

  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    setCurrentPage(1);
  };

  const sidebarItems = [
    { key: "all", label: t.all, count: allNotifications.filter(n => n.highlight).length },
    { key: "content", label: t.educationalContent, count: unreadContentNotifications.length },
    { key: "appointments", label: t.appointments, count: unreadAppointmentNotifications.length },
    { key: "announcements", label: t.systemAnnouncements, count: unreadAnnouncementsNotifications.length },
    { key: "privacy", label: t.dataPrivacy, count: unreadPrivacyNotifications.length },
  ];

  return (
    <section className="notifications-container">
      <div className="notifications-header">
        <h1>{t.notifications}</h1>
        {filtered.length > 0 && (
          <div className="pagination-container">
            <div className="pagination-info">
              <span>Showing {startIndex + 1}-{Math.min(endIndex, filtered.length)} of {filtered.length} records</span>
            </div>
            <div className="pagination-controls">
              <button
                className="pagination-btn pagination-btn--prev"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                <FiChevronLeft />
                Previous
              </button>

              <div className="pagination-numbers">
                {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNumber) => (
                  <button
                    key={pageNumber}
                    className={`pagination-number ${currentPage === pageNumber ? 'pagination-number--active' : ''}`}
                    onClick={() => setCurrentPage(pageNumber)}
                  >
                    {pageNumber}
                  </button>
                ))}
              </div>

              <button
                className="pagination-btn pagination-btn--next"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages || totalPages === 0}
              >
                Next
                <FiChevronRight />
              </button>
            </div>
          </div>
        )}
      </div>
      
      <div className="notifications-content">
        {/* Sidebar */}
        <aside className="notifications-sidebar">
          <ul>
            {sidebarItems.map((item) => (
              <li
                key={item.key}
                className={activeCategory === item.key ? "active" : ""}
                onClick={() => handleCategoryChange(item.key)}
                data-count={item.count > 0 ? item.count : undefined}
              >
                {item.label}
              </li>
            ))}
          </ul>
        </aside>

        {/* Notifications list */}
        <div className="notifications-body">
          {/* Notification Cards */}
          {isLoading && !hasLoaded ? (
            <div className="notifications-skeleton-container">
              {[...Array(5)].map((_, index) => (
                <div className="notification-card-skeleton" key={index}>
                  <div className="skeleton skeleton-notif-icon"></div>
                  <div className="skeleton skeleton-notif-title"></div>
                  <div className="skeleton skeleton-notif-desc"></div>
                  <div className="skeleton skeleton-notif-time"></div>
                </div>
              ))}
            </div>
          ) : paginatedItems.length === 0 ? (
            <div className="empty-state">
              <h3>{t.noNotifications}</h3>
              <p>{t.noNotificationsInCategory}</p>
            </div>
          ) : (
            paginatedItems.map((item) => (
              <div 
                key={item.id} 
                className={`notification-card ${item.highlight ? "unread" : "read"}`}
                onClick={item.onClick}
              >
                <div className="notification-icon">{item.icon}</div>
                
                {item.coverImage && (
                  <div className="notification-image-container">
                    <img src={item.coverImage} alt={item.title} className="notification-cover-image" />
                  </div>
                )}
                
                <div className="notification-text-container">
                  <div className="notification-title" style={{fontSize: '13px', fontWeight: '700', color: '#000000', margin: '0 0 6px', display: 'block', lineHeight: '1.3'}}>{item.title}</div>
                  <p className="notification-desc" style={{fontSize: '12px', fontWeight: '400', color: item.highlight ? '#475569' : '#9ca3af'}}>{item.desc}</p>
                </div>
                
                <div className="notification-meta">
                  <span className="notification-time" style={{fontSize: '11px', fontWeight: '500', color: item.highlight ? '#64748b' : '#d1d5db'}}>{item.time}</span>
                  {item.onDelete && (
                    <button 
                      className="notification-delete-btn" 
                      onClick={item.onDelete}
                      title="Delete notification"
                    >
                      <FiTrash2 />
                    </button>
                  )}
                </div>
              </div>
            )))}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="notif-modal-overlay">
          <div className="notif-modal-content">
            <div className="notif-modal-header">
              <FiAlertTriangle className="notif-modal-icon" />
              <h3>Delete Notification</h3>
            </div>
            <p>Are you sure you want to delete this notification? This action cannot be undone.</p>
            <div className="notif-modal-actions">
              <button 
                className="notif-modal-btn cancel" 
                onClick={() => {
                  setShowDeleteModal(false);
                  setNotifToDelete(null);
                }}
              >
                Cancel
              </button>
              <button 
                className="notif-modal-btn delete" 
                onClick={confirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
